import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { merge } from "lodash";
import { reusableFetch } from "@/utils/useFetch";
import {
  ITenant,
  ITenantFormState,
  ITenantSlice,
  ITenantState,
  IGetAllTenantsDetailsResponse,
  IGetAllTenantsResponse,
  IGetTenantByIdResponse,
} from "@/store/slices/types/tenantSliceTypes";

export const getAllTenants = createAsyncThunk<IGetAllTenantsResponse, unknown>(
  "tenant/getAllTenants",
  async (data, { rejectWithValue }) => {
    const response = await reusableFetch(`admin/tenant/all`, "POST", data, rejectWithValue);
    return response as IGetAllTenantsResponse;
  },
);

// This is for dropdown
export const getAllTenantDetailsList = createAsyncThunk<IGetAllTenantsDetailsResponse, unknown>(
  "tenant/getAllTenantDetailsList",
  async (_data, { rejectWithValue }) => {
    const response = await reusableFetch(`admin/tenant/all-tenant-details`, "GET", null, rejectWithValue);
    return response as IGetAllTenantsDetailsResponse;
  },
);

export const getTenantById = createAsyncThunk<IGetTenantByIdResponse, string>(
  "tenant/getTenantById",
  async (tenantId, { rejectWithValue }) => {
    const response = await reusableFetch(`admin/tenant/${tenantId}`, "GET", null, rejectWithValue);
    return response as IGetTenantByIdResponse;
  },
);

export const updateTenant = createAsyncThunk<unknown, { tenantId: string; payload: unknown }>(
  "tenant/updateTenant",
  async ({ tenantId, payload }, { rejectWithValue }) => {
    const response = await reusableFetch(`admin/tenant/${tenantId}`, "PUT", payload, rejectWithValue);
    return response;
  },
);

export const updateTenantStatus = createAsyncThunk<
  IGetTenantByIdResponse,
  { tenantId: string; field: string; value: boolean }
>("tenant/updateTenantStatus", async ({ tenantId, field, value }, { rejectWithValue }) => {
  const response = await reusableFetch(`admin/tenant/${tenantId}/status`, "PATCH", { [field]: value }, rejectWithValue);
  return response as IGetTenantByIdResponse;
});

const buildInitialForm = (): ITenantFormState => ({
  firstName: "",
  lastName: "",
  email: "",
  address: "",
  ccEmails: [],
  billingName: "",
  billingAddress: "",
  licenceFees: "",
  referralFees: "",
});

const formFromTenant = (tenant: ITenant): ITenantFormState => ({
  firstName: tenant.firstName || "",
  lastName: tenant.lastName || "",
  email: tenant.email || "",
  address: tenant.address || "",
  ccEmails: tenant.ccEmails || [],
  billingName: tenant.billingName || "",
  billingAddress: tenant.billingAddress || "",
  licenceFees: tenant.licenceFees != null ? String(tenant.licenceFees) : "",
  referralFees: tenant.referralFees != null ? String(tenant.referralFees) : "",
});

const initialState: ITenantState = {
  gettingTenants: false,
  gettingTenantsDropdownList: false,
  page: 1,
  tenantDetailsList: [],
  tenants: {
    list: [],
  },
  currentTenant: null,
  gettingTenantById: false,
  updatingTenant: false,
  form: buildInitialForm(),
  showErrors: false,
};

export const tenantSlice = createSlice({
  name: "tenant",
  initialState,
  reducers: {
    saveValue: (state, action) => merge(state, action.payload),
    resetTenantState: () => initialState,
    setTenantForm: (state, action: PayloadAction<Partial<ITenantFormState>>) => {
      state.form = { ...state.form, ...action.payload };
    },
    setTenantShowErrors: (state, action: PayloadAction<boolean>) => {
      state.showErrors = action.payload;
    },
    resetTenantForm: (state) => {
      state.form = buildInitialForm();
      state.currentTenant = null;
      state.showErrors = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllTenants.fulfilled, (state, { payload }: PayloadAction<IGetAllTenantsResponse>) => {
        state.tenants = payload?.data?.data || ({} as ITenantState["tenants"]);
        state.gettingTenants = false;
      })
      .addCase(getAllTenants.pending, (state) => {
        state.gettingTenants = true;
      })
      .addCase(getAllTenants.rejected, (state) => {
        state.gettingTenants = false;
      })
      .addCase(getAllTenantDetailsList.fulfilled, (state, { payload }: PayloadAction<IGetAllTenantsDetailsResponse>) => {
        state.tenantDetailsList = (payload?.data?.data?.list || []) as unknown as ITenantState["tenantDetailsList"]
        state.gettingTenantsDropdownList = false;
      })
      .addCase(getAllTenantDetailsList.pending, (state) => {
        state.gettingTenantsDropdownList = true;
      })
      .addCase(getAllTenantDetailsList.rejected, (state) => {
        state.gettingTenantsDropdownList = false;
      })
      .addCase(getTenantById.pending, (state) => {
        state.gettingTenantById = true;
      })
      .addCase(getTenantById.fulfilled, (state, { payload }: PayloadAction<IGetTenantByIdResponse>) => {
        const tenant = payload?.data?.data || null;
        state.currentTenant = tenant;
        state.gettingTenantById = false;
        state.showErrors = false;
        state.form = tenant ? formFromTenant(tenant) : buildInitialForm();
      })
      .addCase(getTenantById.rejected, (state) => {
        state.gettingTenantById = false;
      })
      .addCase(updateTenant.pending, (state) => {
        state.updatingTenant = true;
      })
      .addCase(updateTenant.fulfilled, (state) => {
        state.updatingTenant = false;
      })
      .addCase(updateTenant.rejected, (state) => {
        state.updatingTenant = false;
      })
      // Updates a single tenant's status field in place so the list is not re-fetched
      .addCase(updateTenantStatus.fulfilled, (state, { payload }: PayloadAction<IGetTenantByIdResponse>) => {
        const updated = payload?.data?.data;
        if (updated?._id && Array.isArray(state.tenants?.list)) {
          state.tenants.list = state.tenants.list.map((tenant) =>
            tenant._id === updated._id ? { ...tenant, ...updated } : tenant,
          );
        }
      });
  },
});

export const { saveValue, resetTenantState, setTenantForm, setTenantShowErrors, resetTenantForm } =
  tenantSlice.actions;

export const tenantStateSelector = (state: ITenantSlice) => state.tenant;
export const tenantSelector = (state: ITenantSlice) => state.tenant.tenants;
